import { forwardRef, isValidElement, cloneElement, type ComponentType, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <IconBadge>. Render logic
// and CSS (incl. the dark-mode glow) ported letter-for-letter from the
// vendor's compiled bundle and its IconBadge CSS — new .rc-icon-badge class
// name, same --ds-icon-badge-* custom properties computed inline per-badge.
//
// `icon` accepts EITHER a bare component reference (`icon={Foo}`,
// including `React.memo`/`forwardRef`-wrapped ones, which are objects with
// a `$$typeof` rather than functions) OR an already-built element
// (`icon={<Foo size={16} />}`) — both forms are real, heavily-used call
// site patterns in this app, so both branches are preserved exactly as the
// vendor implemented them, including cloning an element to override its
// `size`/`style.color` rather than re-creating it.
export interface IconBadgeProps extends HTMLAttributes<HTMLDivElement> {
  icon: ComponentType<{ size?: number; style?: CSSProperties }> | ReactNode;
  color?: string;
  size?: number;
  iconSize?: number;
}

function renderIcon(icon: IconBadgeProps["icon"], color: string, iconSize: number): ReactNode {
  if (isValidElement(icon)) {
    const elementProps = icon.props as { size?: number; style?: CSSProperties };
    return cloneElement(icon, {
      size: elementProps.size ?? iconSize,
      style: { color, ...elementProps.style },
    } as Record<string, unknown>);
  }
  const isComponent = typeof icon === "function" || (typeof icon === "object" && icon !== null && "$$typeof" in icon);
  if (isComponent) {
    const IconComponent = icon as ComponentType<{ size?: number; style?: CSSProperties }>;
    return <IconComponent size={iconSize} style={{ color }} />;
  }
  return icon;
}

export const IconBadge = forwardRef<HTMLDivElement, IconBadgeProps>(function IconBadge(
  { icon, color = "currentColor", size = 30, iconSize = 14, className, style, ...rest },
  ref
) {
  const badgeStyle = {
    "--rc-icon-badge-size": `${size}px`,
    "--rc-icon-badge-radius": `${Math.round(size * 0.23)}px`,
    "--rc-icon-badge-color": color,
    ...style,
  } as CSSProperties;

  return (
    <div ref={ref} className={cn("rc-icon-badge", className)} style={badgeStyle} {...rest}>
      {renderIcon(icon, color, iconSize)}
    </div>
  );
});
